import {
  ManageGroupId,
  StartPage,
  type LevelOne,
  type ManageGroup,
  type ManageMain,
  type ManagePageId,
  type RoleKey,
} from './me.js';

/**
 * System Admins are the privileged group that sees every page (APP-134): every page any role has
 * opens for them with company-wide scope, while what they can change stays with their own roles.
 * Page gates, Level 1, Search & jump, Preview's samples, Help and the AI audiences all ask this.
 */
export function seesEveryPage(roles: readonly RoleKey[]): boolean {
  return roles.includes('system_admin');
}

/**
 * APP-234: what Manage needs to know beyond a person's roles. Each is the answer the Spot pages
 * already give (`GET /v1/spot`'s flags), worked out on the server for the person shown. All absent
 * in a sample preview, where Manage follows the previewed roles alone.
 */
export interface ManageFacts {
  /** On the Spot Committee today (`spotReviewerIds`, the Spot pages' `reviewer`). */
  spotCommittee?: boolean;
  /**
   * The directory manager, or a standing appointee, of a colleague on a sent Spot nomination
   * (`managesAnySpotNominee`, half of the Spot pages' `allWork`).
   */
  spotTeam?: boolean;
  /** A colleague's Spot decision waits on them as its supervisor now (`supervisorWork`). */
  spotDecisions?: boolean;
  /** They decide Spot funding and a funding request waits (`fundingWork`). */
  spotFunding?: boolean;
  /**
   * APP-245: they hold a current released letter, to hand out or handed out, by the holder scope
   * Letters to hand out lists (`releasedHolderLineScope`): never their own (APP-238).
   */
  lettersToHandOut?: boolean;
}

/**
 * Each Manage page's address (APP-255): the web's tab and route for it (`MANAGE_PAGE`) and the
 * server's links to it (Ask the data's read-only reply) read this one table, so a link can't name
 * an address the tab has left. APP-236 put the Executive group's pages under `/manage/executive`
 * (the old `/executive/…` addresses redirect); Approvals and Letters to hand out keep their one
 * address (D5, APP-245 D1). The words on a tab or a link stay with whoever shows it.
 */
export const MANAGE_PAGE_HREF: Readonly<Record<ManagePageId, string>> = {
  'spot-review': '/manage/spot/review',
  'spot-all': '/manage/spot/all',
  'spot-letters': '/manage/spot/letters',
  'spot-statements': '/manage/spot/statements',
  'spot-decisions': '/manage/spot/decisions',
  queue: '/manage/queue',
  letters: '/manage/queue/letters',
  'earning-statements': '/manage/earning-statements',
  shareworks: '/manage/shareworks',
  payroll: '/manage/payroll',
  'budget-pools': '/manage/executive/budget-pools',
  values: '/manage/executive/values',
  'spot-funding': '/manage/spot/funding',
  approvals: '/approvals',
  'letters-to-hand-out': '/letters-to-hand-out',
};

/**
 * Manage's groups in the standard order (APP-236, Tom 2026-10-29): job roles before side duties.
 * Row 1 shows a person's main work first, then the rest in this order (`manageLayoutFor`); My
 * preferences › Main work lists their groups in it; and a page several groups offer sits in the
 * first of them (APP-234 D2a).
 */
export const MANAGE_GROUP_ORDER: readonly ManageGroupId[] = [
  'executive',
  'stock_admin',
  'payroll',
  'sector_lead',
  'approver',
  'committee',
  'team',
];

/**
 * Manage's pages for a role mix and its facts, grouped by the role each page is for, in the
 * standard order (APP-234, Tom 2026-10-28, option B; APP-236, Tom 2026-10-29, option C). A page
 * shows exactly when its page's Spot flag or role allows it; this is navigation only, and every
 * page keeps its own web and server check.
 *
 * - Executive: Budget pools, Approvals and Values & requesters for whoever had the Executive item
 *   (Executives, and System Admins, who see every page, APP-134), and Spot funding, only while a
 *   request waits, for whoever decides it (Executives, or a Stock Admin while acting for
 *   Executives is on).
 * - Stock Admin: the queue (its sidebar views stay inside it), Letters, Earning statements and
 *   Shareworks, for the team and for System Admins. APP-235: appointing a supervisor is on Spot
 *   awards' "No manager set" row, not a page.
 * - Payroll: the Payroll queue, for Payroll and System Admins.
 * - Sector Lead, PAA/TAA approver: Approvals, unless the person has the Executive group's, which
 *   already holds it.
 * - Committee: Review, Spot awards and Spot earning statements for a member. APP-249 (the Spot
 *   redesign's canvas, S2 and S9): no Spot letters tab, since the Stock Admin team checks and
 *   releases them (APP-244); a member still reads a letter from its award's row in Spot awards,
 *   and the page still opens for them. APP-236: Spot awards alone for anyone else who reads every
 *   row (the Stock Admin team and System Admins): the page sits under the role it's for, whoever
 *   reads it.
 * - My team: Spot decisions while one waits on them; Spot awards for a manager of a nominee who
 *   doesn't read every row anyway (their own people only).
 * - APP-245: Letters to hand out for anyone holding letters, even as their only Manage page:
 *   under Sector Lead for a Sector Lead or delegate, else under My team (D2, D3 [default]),
 *   after the Spot pages, as the S7 board draws it (APP-248 D12 puts the other order to Tom).
 *
 * A page shows once (D2a): Approvals, offered to Sector Leads and to PAA/TAA approvers, sits in the
 * first of those groups the person has. Groups left empty are dropped.
 */
export function manageMenuFor(roles: readonly RoleKey[], facts: ManageFacts = {}): ManageGroup[] {
  const has = (...r: RoleKey[]) => r.some((x) => roles.includes(x));
  const everyPage = seesEveryPage(roles);
  const stockAdminPages = has('stock_admin') || everyPage;
  // Whoever reads every row of All Spot awards (`readsEverySpotAward`): the committee, the Stock
  // Admin team and System Admins. A manager's own Spot awards is then the same page.
  const readsEverySpot = facts.spotCommittee === true || stockAdminPages;
  // Whoever had the Executive item before APP-236; it gates Sector Lead's and PAA/TAA approver's
  // Approvals exactly as before.
  const executiveItem = has('executive') || everyPage;
  // APP-245 D2: offered to both groups; a page shows once, so a lead's or delegate's sits under
  // Sector Lead, the first of them, and everyone else's under My team.
  const holder = facts.lettersToHandOut === true;
  const offered: Record<ManageGroupId, ManagePageId[]> = {
    executive: [
      ...(executiveItem ? (['budget-pools', 'approvals', 'values'] as const) : []),
      ...(facts.spotFunding === true ? (['spot-funding'] as const) : []),
    ],
    stock_admin: stockAdminPages ? ['queue', 'letters', 'earning-statements', 'shareworks'] : [],
    payroll: has('payroll') || everyPage ? ['payroll'] : [],
    sector_lead: [
      ...(has('sector_lead') && !executiveItem ? (['approvals'] as const) : []),
      ...(holder && has('sector_lead', 'delegate') ? (['letters-to-hand-out'] as const) : []),
    ],
    approver: has('paa_taa_approver') && !executiveItem ? ['approvals'] : [],
    committee:
      facts.spotCommittee === true
        ? ['spot-review', 'spot-all', 'spot-statements']
        : readsEverySpot
          ? ['spot-all']
          : [],
    team: [
      ...(facts.spotDecisions === true ? (['spot-decisions'] as const) : []),
      ...(facts.spotTeam === true && !readsEverySpot ? (['spot-all'] as const) : []),
      ...(holder ? (['letters-to-hand-out'] as const) : []),
    ],
  };
  const seen = new Set<ManagePageId>();
  const groups: ManageGroup[] = [];
  for (const id of MANAGE_GROUP_ORDER) {
    const pages = offered[id].filter((page) => !seen.has(page));
    for (const page of pages) seen.add(page);
    if (pages.length > 0) groups.push({ id, pages });
  }
  return groups;
}

/**
 * Whether a person holds the role a Manage group is for, by their own roles and Spot facts: never
 * by a System Admin's see-every-page access (APP-134), nor by a Stock Admin's acting for
 * Executives, whose Executive group comes and goes with each funding request. APP-245 D5: nor by
 * holding letters, a side duty that comes and goes with each release.
 */
function holdsGroup(id: ManageGroupId, roles: readonly RoleKey[], facts: ManageFacts): boolean {
  switch (id) {
    case 'executive':
      return roles.includes('executive');
    case 'stock_admin':
      return roles.includes('stock_admin');
    case 'payroll':
      return roles.includes('payroll');
    case 'sector_lead':
      return roles.includes('sector_lead');
    case 'approver':
      return roles.includes('paa_taa_approver');
    case 'committee':
      return facts.spotCommittee === true;
    case 'team':
      return facts.spotTeam === true || facts.spotDecisions === true;
  }
}

/** How Manage is laid out for a person: what `GET /me` sends as `manage` and `manageMain`. */
export interface ManageLayout {
  /** Their groups in display order: main work first, then the rest in the standard order. */
  groups: ManageGroup[];
  /** Their main work and what Automatic means; null when they have no Manage. */
  main: ManageMain | null;
}

/**
 * APP-236 §4, the one rule for main work (Tom 2026-10-29, P1 = b: derived, plus a My preferences
 * choice). Manage shows the main work group first, then a divider, then the person's other groups
 * in the standard order; the header's Manage and `/manage` open its first page.
 *
 * - **Automatic** is the first of their groups, in the standard order, whose role they hold
 *   themselves (`holdsGroup`): "executives are executive, stock admins are stock admins".
 * - **[default]** A System Admin with none of their own (every group comes from seeing every
 *   page) gets Stock Admin: the standard order would give them Executive, which isn't their work.
 *   Failing that, their first group.
 * - **Saved** (`my.mainWork`, a group id or null for Automatic) wins while they have that group. A
 *   group they no longer have is ignored, not cleared, so it comes back when they have it again,
 *   as `startPageFor` treats a start page.
 *
 * Order and landing only: main work never changes who can open what. A sample preview passes no
 * saved choice, so it shows the derived rule.
 */
export function manageLayoutFor(
  roles: readonly RoleKey[],
  facts: ManageFacts = {},
  saved: unknown = null,
): ManageLayout {
  const groups = manageMenuFor(roles, facts);
  const ids = groups.map((g) => g.id);
  const automatic =
    ids.find((id) => holdsGroup(id, roles, facts)) ??
    (ids.includes('stock_admin') ? 'stock_admin' : ids[0]);
  if (automatic === undefined) return { groups, main: null };
  const chosen = ManageGroupId.safeParse(saved);
  const id = chosen.success && ids.includes(chosen.data) ? chosen.data : automatic;
  return {
    groups: [...groups.filter((g) => g.id === id), ...groups.filter((g) => g.id !== id)],
    main: { id, automatic, choices: ids },
  };
}

/**
 * Level 1 for a role mix, in the fixed order Home · My rewards · Recognize · Manage · Insights ·
 * Settings (APP-234 adds Manage; APP-236 moves Executive's pages into it). Items a person can't use
 * are left out, not greyed (REQUIREMENTS-MAP "Role-based menus"). Manage shows when the person has
 * at least one of its pages (`manageMenuFor`). Insights follows the domain's report rights:
 * Executives, Finance, Stock Admins and Sector Leads and System Admins (APP-097: a System Admin
 * alone sees Insights too, matching the API's report rights; the audit log stays under Settings).
 *
 * The one rule behind `Me.nav`. It lives with the contract so the web's Preview as panel can say
 * "Header will show …" from exactly what the server will send (APP-059). Without `facts` (a sample
 * preview, the Preview as panel, Admin › People), Manage follows the roles alone.
 */
export function levelOneFor(roles: readonly RoleKey[], facts: ManageFacts = {}): LevelOne[] {
  const has = (...r: RoleKey[]) => r.some((x) => roles.includes(x));
  const items: LevelOne[] = ['home', 'my-rewards', 'recognize'];
  if (manageMenuFor(roles, facts).length > 0) items.push('manage');
  if (has('executive', 'finance', 'stock_admin', 'sector_lead', 'system_admin'))
    items.push('insights');
  items.push('settings');
  return items;
}

/**
 * Where sign-in lands (SET1, APP-081): the saved start page while it's still one of the person's
 * Level 1 items, otherwise Home. Nothing saved, or a role that went away, is Home. APP-236: a start
 * page saved as Executive is Manage now, which opens on an Executive's main work, the Executive
 * group.
 */
export function startPageFor(saved: unknown, nav: readonly LevelOne[]): StartPage {
  const parsed = StartPage.safeParse(saved === 'executive' ? 'manage' : saved);
  return parsed.success && nav.includes(parsed.data) ? parsed.data : 'home';
}

/**
 * The start pages a person may choose, from their Level 1 as the server sends it (`Me.nav`): its
 * items, less Settings, and the legacy Admin value for existing preferences (APP-094).
 */
export function startPagesIn(nav: readonly LevelOne[], roles: readonly RoleKey[]): StartPage[] {
  return StartPage.options.filter(
    (page) => nav.includes(page) || (page === 'admin' && roles.includes('system_admin')),
  );
}

/**
 * Start choices for a role mix and its Manage facts: Manage is one while they have it (APP-236).
 * Start choices retain the legacy Admin value for existing preferences (APP-094).
 */
export function startPagesFor(roles: readonly RoleKey[], facts: ManageFacts = {}): StartPage[] {
  return startPagesIn(levelOneFor(roles, facts), roles);
}
